import { useMemo, useRef, useState } from 'react';
import { Spinner, Text } from '@belong/ui';
import clsx from 'clsx';
import { noop } from 'es-toolkit/compat';
import {
  ApplicationDetails,
  EmployeePublicModel,
  UnitBasicInfoModel,
  UnitLeadsListingModel,
  UnitListingInfo,
} from 'src/api/models';
import {
  FeedbackMessage,
  FeedbackMessageRef,
} from 'src/components/feedback-message/feedback-message';
import { SignUpSignInModal } from 'src/features/auth/components/sign-up-sign-in-modal/sign-up-sign-in-modal';
import { useUser } from 'src/features/users/useUser';
import { useModal } from 'src/hooks/useModal';
import { ActionButtons } from './components/action-buttons';
import BelongRequirements from './components/belong-requirements';
import { Benefits } from './components/benefits';
import { ExistingApplications } from './components/existing-applications';
import {
  useCreateResidentApplication,
  useResidentApplications,
} from './get-started.hooks';
import { GET_STARTED_STRINGS } from './get-started.strings';
import styles from './index.module.css';

export type GetStartedProps = {
  homeId: string;
  residentGrowth: EmployeePublicModel;
  homeBasicInfo: UnitBasicInfoModel | null;
  homeInterests: UnitLeadsListingModel | null;
  continueApplication: (
    applicationId: string,
    isMainApplicant: boolean,
  ) => void;
  startApplication: (applicationId: string) => void;
  listingInfo: UnitListingInfo | null;
  currentPayoutPlanType?: 'Variable' | 'Fixed' | 'VariableNoMinimum';
};

export default function GetStartedPage({
  homeId,
  homeBasicInfo,
  continueApplication,
  startApplication,
  listingInfo,
  currentPayoutPlanType,
}: GetStartedProps) {
  const feedbackMessageRef = useRef<FeedbackMessageRef>(null);

  const { user, refetchUser } = useUser();
  const [
    isAuthenticationModalOpen,
    openAuthenticationModal,
    closeAuthenticationModal,
  ] = useModal();
  const onSuccessfullAuthCallbackRef = useRef(noop);

  const [userSelectedApplication, setUserSelectedApplication] =
    useState<ApplicationDetails>();
  const { applications: existingApplications, isLoadingApplications } =
    useResidentApplications(!user);

  const autoSelectedApplication = useMemo(() => {
    if (!Array.isArray(existingApplications)) return undefined;
    return [...existingApplications]
      .reverse()
      .find(
        (a) =>
          a.basicInfo?.status === 'Started' &&
          !!a.listingDetails?.find(
            (listingDetail) => listingDetail.home?.unitId === homeId,
          ),
      );
  }, [existingApplications, homeId]);

  const selectedApplication =
    userSelectedApplication ?? autoSelectedApplication;

  const { createApplication, isCreatingApplication } =
    useCreateResidentApplication({
      onSuccess: function ({ applicationId }: { applicationId: string }) {
        startApplication(applicationId);
      },
      onError: () => {
        feedbackMessageRef.current?.showMessage(
          'Something went wrong. Please try again',
          'error',
        );
      },
    });

  function selectApplication(app: ApplicationDetails) {
    setUserSelectedApplication(app);
    const selectedApplicationId = app?.basicInfo?.applicationId;
    if (!selectedApplicationId) return;

    if (app?.basicInfo?.status === 'Started') {
      continueApplication(
        selectedApplicationId,
        app.applicants?.mainApplicant?.residentId === user?.userId,
      );
    } else {
      startApplication(selectedApplicationId);
    }
  }

  function resumeApplication() {
    onSuccessfullAuthCallbackRef.current = refetchUser;
    openAuthenticationModal();
  }

  function startNewApplication() {
    if (!user) {
      onSuccessfullAuthCallbackRef.current = createApplication;
      openAuthenticationModal();
      return;
    }
    createApplication();
  }

  const shouldShowExistingApplications =
    !!user && !!existingApplications && existingApplications.length > 0;

  return (
    <section className="mb-3xl container flex flex-col">
      <div className="absolute left-0 top-0 z-0 h-[500px] w-full overflow-hidden">
        {/* Background image with zoom */}
        <div
          className={clsx('absolute inset-0 bg-cover bg-center', styles.zoomIn)}
          style={{
            backgroundImage: `url(${homeBasicInfo?.bannerImageUrl || ''})`,
          }}
        />

        <div className="absolute inset-0 z-20 bg-gradient-to-b from-black to-transparent opacity-50" />
      </div>
      {(isLoadingApplications || isCreatingApplication) && <Spinner />}

      <FeedbackMessage ref={feedbackMessageRef} />
      <div className="relative flex w-full flex-col">
        <Text
          variant="h2"
          fontWeight="semibold"
          className="mb-sm mt-xl whitespace-pre-line text-center text-white"
        >
          {GET_STARTED_STRINGS.title}
        </Text>
        <Text className="mb-2xl whitespace-pre-line text-center  text-white">
          {homeBasicInfo?.address?.streetAddress},{' '}
          {homeBasicInfo?.address?.city}
        </Text>
        <BelongRequirements
          moveInDate={listingInfo?.availableOn}
          currentPayoutPlanType={currentPayoutPlanType}
        />
        {/* In mobile we move the existing applications and action buttons to the bottom of the screen */}
        <div className="mt-lg gap-sm flex flex-col-reverse">
          <Benefits className="mt-xl" />
          <div>
            {shouldShowExistingApplications && (
              <ExistingApplications
                applications={existingApplications}
                onApplicationPress={selectApplication}
                userEmail={user.email}
                className="my-xl max-w-flow-lg md:mb-sm mx-auto"
              />
            )}

            <ActionButtons
              shouldShowResumeApplicationButton={!user}
              onResumeApplicationPress={resumeApplication}
              existingApplications={existingApplications}
              selectedApplication={selectedApplication}
              onStartNewApplicationPress={startNewApplication}
            />
          </div>
        </div>
      </div>
      {!user && (
        <SignUpSignInModal
          initialActiveTab="sign-in"
          isOpen={isAuthenticationModalOpen}
          onDismiss={closeAuthenticationModal}
          onSignInSuccess={() => onSuccessfullAuthCallbackRef.current()}
          onSignUpSuccess={() => onSuccessfullAuthCallbackRef.current()}
          shouldAskForSignUpPassword
          shouldAskForSignUpSmsConsent
          size="large"
          header={<div className="mt-sm w-full" />}
        />
      )}
    </section>
  );
}
